// frontend/src/utils/reference/topicTracker.js

function normalizeText(value = '') {
  return String(value || '').toLowerCase().trim();
}

function wordCount(value = '') {
  const normalized = normalizeText(value);
  if (!normalized) return 0;
  return normalized.split(/\s+/).filter(Boolean).length;
}

// 🔹 Existing topic detection (keep it simple and bounded)
const TOPIC_KEYWORDS = [
  { topic: 'relationships', keywords: ['mother', 'father', 'mom', 'dad', 'daughter', 'son', 'brother', 'sister', 'friend', 'friends', 'family'] },
  { topic: 'debugging', keywords: ['debug', 'bug', 'error', 'syntax', 'compile', 'trace', 'log', 'broken', 'failing'] },
  { topic: 'writing', keywords: ['draft', 'rewrite', 'edit', 'revise', 'tighten', 'wording', 'phrase'] },
  { topic: 'planning', keywords: ['plan', 'roadmap', 'next steps', 'strategy', 'approach'] },
  { topic: 'approval', keywords: ['final', 'done', 'posted', 'leave it', 'ship it', 'approved'] },
];

function detectTopicFromMessage(message = '') {
  const lower = normalizeText(message);
  if (!lower) return null;

  for (const entry of TOPIC_KEYWORDS) {
    const matched = entry.keywords.find((keyword) =>
      lower.includes(normalizeText(keyword))
    );
    if (matched) {
      return {
        topic: entry.topic,
        matchedBy: matched,
        source: 'keyword_match',
      };
    }
  }

  return null;
}

// 🔹 NEW: structural signals only (NO domain parsing)

function isClarificationTurn(message = '') {
  const lower = normalizeText(message);

  return (
    lower.includes('that is what i mean') ||
    lower.includes("that's what i mean") ||
    lower.includes('i mean') ||
    lower.includes('what i mean') ||
    lower.includes('no that') ||
    lower.includes('no its') ||
    lower.includes('no it is')
  );
}

function isShortTurn(message = '') {
  return wordCount(message) <= 5;
}

function isContinuationTurn(message = '') {
  if (!message) return false;

  if (isClarificationTurn(message)) return true;
  if (isShortTurn(message)) return true;

  return false;
}

function isLikelyShift({
  message = '',
  detectedTopic = null,
  currentTopic = null,
} = {}) {
  const lower = normalizeText(message);
  if (!lower) return false;

  // Strong topic change
  if (
    detectedTopic?.topic &&
    currentTopic?.topic &&
    detectedTopic.topic !== currentTopic.topic
  ) {
    return true;
  }

  // Longer sentence + no clarification → likely new thought
  if (
    wordCount(lower) >= 7 &&
    !isClarificationTurn(lower)
  ) {
    return true;
  }

  return false;
}

// 🔹 MAIN STATE BUILDER

function buildTopicState({
  message = '',
  currentTopic = null,
  currentThread = null,
} = {}) {
  const detectedTopic = detectTopicFromMessage(message);

  const clarificationLikely = isClarificationTurn(message);
  const continuationLikely = isContinuationTurn(message);

  const detectedShift = isLikelyShift({
    message,
    detectedTopic,
    currentTopic,
  });

  const hasActiveContext = !!currentTopic;

  const shouldCarryForward =
    hasActiveContext &&
    !detectedShift &&
    (continuationLikely || clarificationLikely);

  const lastReferencedTopic =
    detectedTopic || currentTopic || null;

  const lastReferencedThread =
    clarificationLikely
      ? { thread: 'clarification', source: 'continuity' }
      : shouldCarryForward
        ? { thread: 'continuation', source: 'continuity' }
        : currentThread || null;

  return {
    lastReferencedTopic,
    lastReferencedThread,
    continuity: {
      continuationLikely,
      clarificationLikely,
      detectedShift,
      shouldCarryForward,
      topicStable:
        !!currentTopic?.topic &&
        !!lastReferencedTopic?.topic &&
        currentTopic.topic === lastReferencedTopic.topic,
    },
  };
}

function resolveLastReferencedTopic({
  message = '',
  currentTopic = null,
} = {}) {
  return buildTopicState({
    message,
    currentTopic,
  }).lastReferencedTopic;
}

export { buildTopicState, resolveLastReferencedTopic };